import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AppButton, Box } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import PlanCardTemplateActions from '@/components/elements/PlanCard/PlanCardTemplateActions.tsx';
import { useCurrentOrganization, useHasAccess, useHistory } from '@/modules/core/hooks';
import { useForceOpenModalStore } from '@/modules/HealthPlan/HealthPlansList/stores';
import { PlanCardAction, PlanCardMode } from '@/modules/plan/plan.types';

import { useEditModeStore } from '../stores';

interface Props {
  updateButtonRef?: React.RefObject<HTMLButtonElement | null | undefined>;
  isTemplateMode?: boolean;
  isHealthPlanActive?: boolean
}

const HealthPlanActivation: React.FC<Props> = ({ updateButtonRef, isTemplateMode, isHealthPlanActive }) => {
  const {
    observingOrganization: { type },
  } = useCurrentOrganization();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const handleSetModalVisible = useForceOpenModalStore((state) => state.setState);
  const setEditMode = useEditModeStore((state) => state.setEditMode);
  const isEditMode = useEditModeStore((state) => state.editMode);

  const handleSetEditMode = useCallback(() => {
    setEditMode(true);
  }, [setEditMode]);

  const isHasEditPermission = useHasAccess([
    {
      permission: Permissions.SET_UP_HEALTH_PLAN,
    },
  ]);

  const handleSaveUpdates = useCallback(() => {
    if (updateButtonRef?.current) {
      updateButtonRef?.current.click();
    }
  }, [updateButtonRef]);

  const isSystem = useMemo(() => type === OrganizationTypes.system, [type]);
  const isPartner = useMemo(() => type === OrganizationTypes.partner, [type]);
  const isDistributor = useMemo(() => type === OrganizationTypes.distributor, [type]);

  const isPlansHidden = useMemo(() => isSystem || isPartner || isDistributor, [isPartner, isSystem, isDistributor]);
  const handleSelectAction = useCallback(
    (value: PlanCardAction) => {
      handleSetModalVisible(value, id);
      history.push(ROUTES.HEALTH_PLANS);
    },
    [handleSetModalVisible, history, id]
  );

  if (!isHasEditPermission) {
    return null;
  }

  return (
    <Box direction="row" align="end" justify="end" gap="spacing12">
      {isEditMode ? (
        <AppButton testId="template_edit_button" onClick={handleSaveUpdates} width="130px">
          Save Changes
        </AppButton>
      ) : (
        <Box direction="row" gap="spacing12">
          {
            isTemplateMode && (
              <PlanCardTemplateActions
                isStandaloneMode
                isPlansHidden={isPlansHidden}
                onSelectAction={handleSelectAction}
                mode={PlanCardMode.HealthPlan}
              />
            )
          }
          <AppButton
            testId="template_edit_button"
            onClick={handleSetEditMode}
            width="100px"
            buttonType="secondary"
          >
            Edit
          </AppButton>
        </Box>
      )}
    </Box>
  );
};

export default HealthPlanActivation;
